<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">
        {{ editing ? "Editar contrato" : (isRenewal ? "Renovar contrato" : "Nuevo contrato") }}
      </h1>
    </div>

    <form @submit.prevent="save" novalidate>
      <wz-card :title="editing ? 'Datos del contrato' : 'Datos del nuevo contrato'" :body-padding="false">

        <!-- Alertas de contratos vigentes: visibles en TODOS los tabs -->
        <div v-if="hasActiveContractInSelectedCompany || otherActiveContractsText" class="px-4 pt-4">
          <wz-alert v-if="hasActiveContractInSelectedCompany" variant="danger" icon="ri-error-warning-line" class="wz-alert--danger w-full mb-3">
            Este colaborador ya tiene un contrato vigente en la empresa seleccionada. Debe cesarlo primero.
          </wz-alert>
          <wz-alert v-if="otherActiveContractsText" variant="danger" icon="ri-error-warning-line" class="wz-alert--danger w-full mb-3">
            Este colaborador tiene contratos vigentes en: <strong>{{ otherActiveContractsText }}</strong>.
            Puede crear otro en una empresa distinta.
          </wz-alert>
        </div>

        <!-- Renovación de contrato vencido: motivo con el que se cierra el contrato anterior. -->
        <div v-if="isRenewal && renewOriginExpired" class="px-4 pt-4">
          <wz-alert variant="info" icon="ri-information-line" class="mb-3">
            El contrato anterior está vencido. Elegí el motivo de cese con el que se cerrará.
            Con <strong>fin de contrato</strong> y reingreso ≤ {{ renewalMaxGapDays }} días se mantiene la antigüedad;
            cualquier otro motivo reinicia el período.
          </wz-alert>
          <div class="w-full md:w-1/2 mb-1">
            <wz-select
              label="Motivo del cese anterior"
              required
              :items="cessationReasons"
              v-model="form.cessationReasonId"
              defaultOption="Seleccione motivo"
            />
          </div>
        </div>

        <wz-tabs :items="tabItems" v-model="activeTab" variant="underline" style="padding: 0 20px;">

          <!-- Tab: Datos del contrato -->
          <template #contract>
            <ContractDataTab
              :form="form"
              :v="$v.form"
              :contract-duration-list="contractDurationList"
              :employee-list="employeeList"
              :employee-info="employeeInfo"
              :is-indefinite="isIndefiniteContract"
              :lock-employee="isRenewal"
              :lock-start-date="editing"
              :duration-error="durationExceedsMaxByType"
              :duration-max-months="durationMaxMonths"
              @employee-selected="onEmployeeSelected"
              @probation-error="probationError = $event"
            />
          </template>

          <!-- Tab: Ubicación laboral -->
          <template #location>
            <WorkLocationTab
              :form="form"
              :v="$v.form"
              :companies="companies"
              :work-sites="workSites"
              :areas="areas"
              :positions="positions"
              :employees="managers"
              :schedules="schedules"
              :channels="channels"
              :expense-lines="expenseLines"
              :lock-company="isRenewal"
              @company-changed="onCompanyChanged"
            />
          </template>

          <!-- Tab: Condiciones laborales -->
          <template #conditions>
            <ConditionsTab
              :form="form"
              :v="$v.form"
              :dependent-summary="dependentSummary"
              :employee-id="form.employeeId"
              :payroll-types="payrollTypes"
              :base-salary-locked="salaryLockedByAdenda"
            />
          </template>

          <!-- Tab: Seguros y salud -->
          <template #insurance>
            <InsuranceTab
              :form="form"
              :v="$v.form"
            />
          </template>

        </wz-tabs>

        <!-- CDEF010: campos personalizados del contrato (solo en edición; el asistente de alta no cambia) -->
        <div v-if="!isAlta && $route.params.id" class="px-4">
          <hr class="my-3" />
          <custom-fields-section entity="CONTRATO" :entity-id="Number($route.params.id)" />
        </div>

        <!-- Adenda opcional (solo en renovación): se sube al NUEVO contrato tras crear la renovación. -->
        <div v-if="isRenewal" class="px-4">
          <hr class="my-3" />
          <h6 class="mb-2 font-bold">Adenda (opcional)</h6>
          <div class="flex flex-wrap -mx-2">
            <!-- ¿Qué campos cambia esta adenda? DEF-004: componente compartido con detalle y masivo. -->
            <div class="w-full px-2 mb-3">
              <AddendaFieldsForm ref="adendaFields" :key="adendaFieldsKey" v-model="adendaFields" :company-id="form.companyId" optional />
            </div>
            <div v-if="adendaHasChanges" class="w-full md:w-1/2 px-2 mb-3">
              <wz-datepicker label="Fecha efectiva *" v-model="adendaEffectiveDate" :max="adendaMaxEffectiveDate" />
              <small v-if="adendaHasNonDateAware" class="text-muted block mt-1">
                Los cambios de área, canal, línea de gasto, sede, jefe directo o responsable solo pueden aplicarse a partir de hoy.
              </small>
            </div>
            <div class="w-full md:w-1/2 px-2 mb-3">
              <label class="text-muted block mb-1">Motivo <small class="text-muted">(opcional)</small></label>
              <input v-model="adendaReason" type="text" maxlength="500"
                class="w-full mt-1" placeholder="Ej.: Ascenso, ajuste anual…"
                style="box-sizing:border-box; padding:8px; border:1px solid var(--admin-border-color); border-radius:6px; font-family:inherit; font-size:14px;" />
            </div>
            <div class="w-full md:w-1/2 px-2 mb-3">
              <label class="text-muted block mb-1">Archivo <small class="text-muted">(PDF — máx. 100MB)</small></label>
              <wz-file-input
                :key="addendumFileKey"
                class="w-full"
                id-file="renewal-addendum-upload"
                :accept-file="['.pdf']"
                @onChange="onAddendumFileChange"
              />
            </div>
            <div class="w-full md:w-1/2 px-2 mb-3">
              <label class="text-muted block mb-1">Descripción</label>
              <textarea
                v-model="addendumDescription"
                class="w-full mt-1"
                rows="3"
                maxlength="500"
                placeholder="Descripción de la adenda"
                style="box-sizing:border-box; padding:8px; border:1px solid var(--admin-border-color); border-radius:6px; resize:vertical; font-family:inherit; font-size:14px;"
              ></textarea>
              <small class="text-muted d-block text-right">{{ addendumDescription.length }}/500</small>
            </div>
          </div>
        </div>

        <div class="flex justify-between items-center mt-3 px-4 pb-3">
          <!-- Indicador de progreso del semi-wizard (solo en alta). -->
          <span v-if="isAlta" class="text-muted" style="font-size: 13px;">
            Paso {{ currentTabIndex + 1 }} de {{ tabItems.length }}
          </span>
          <span v-else></span>

          <div class="flex gap-2">
            <wz-button variant="light" @click="$router.push({ name: 'contracts-list' })">
              {{ editing ? "Volver" : "Cancelar" }}
            </wz-button>
            <!-- Alta + tab no final: "Siguiente" (valida el tab actual y avanza). -->
            <wz-button
              v-if="isAlta && !isLastTab"
              type="button"
              variant="primary"
              class="ml-2"
              :disabled="currentTabBlocked"
              @click="nextTab"
            >
              Siguiente
            </wz-button>
            <!-- Último tab (o edición): "Guardar" (submit, con las reglas de negocio de siempre). -->
            <wz-button
              v-else
              type="submit"
              variant="primary"
              class="ml-2"
              :disabled="(hasActiveContractInSelectedCompany && !editing) || probationError || durationExceedsMaxByType"
            >
              Guardar
            </wz-button>
          </div>
        </div>

      </wz-card>
    </form>

    <wz-card v-if="isCeased && cessationDocuments.length" title="Documentos de cese" class="mt-3">
      <ul style="list-style:none; padding:0; margin:0;">
        <li
          v-for="doc in cessationDocuments"
          :key="doc.id"
          class="flex items-center justify-between py-2"
          style="border-bottom:1px solid var(--admin-border-color)"
        >
          <span><i class="ri-file-text-line mr-2"></i>{{ doc.name }}</span>
          <wz-button variant="outline" size="sm" @click="downloadCessationDoc(doc)">
            <i class="ri-download-2-line mr-1"></i>Descargar
          </wz-button>
        </li>
      </ul>
    </wz-card>
  </div>
</template>

<script>
import { Alert, Util } from "@wizepe/wizeui"
import * as moment from 'moment'
import fileDownload from 'js-file-download'
import { required, numeric } from 'vuelidate/lib/validators'

import {
  EmployeeService,
  CompanyService,
  AreaService,
  PositionService,
  WorkSiteService,
  ContractsService,
  WorkScheduleService,
  EmployeeDependentService,
  ChannelService,
  ExpenseLineService,
  PayrollTypeService,
  CessationReasonService,
} from '@/core/services'
import ContractAddendumService from '@/core/services/ContractAddendumService'
import { Constants } from '@/core/utils'

import ContractDataTab  from './tabs/ContractDataTab.vue'
import WorkLocationTab  from './tabs/WorkLocationTab.vue'
import ConditionsTab    from './tabs/ConditionsTab.vue'
import InsuranceTab     from './tabs/InsuranceTab.vue'
import CustomFieldsSection from '@/core/components/CustomFieldsSection.vue'
import AddendaFieldsForm, { NON_DATE_AWARE_FIELDS } from './components/AddendaFieldsForm.vue'

// Plazo máximo legal por tipo de contrato (Art. 74 D.S. 003-97-TR). Espeja MAX_DURATION_MONTHS_BY_TYPE
// del backend (ContractService). Los tipos ausentes (01, 02, 07-12, 14) no tienen tope.
const MAX_DURATION_MONTHS_BY_TYPE = { '03': 36, '04': 60, '05': 24, '06': 6, '13': 36 }

// Adenda opcional en la renovación: mismo criterio que el backend (PDF, 100MB).
const MAX_ADDENDUM_SIZE = 100 * 1024 * 1024

export default {
  components: {
    CustomFieldsSection,
    ContractDataTab,
    WorkLocationTab,
    ConditionsTab,
    InsuranceTab,
    AddendaFieldsForm,
  },

  data() {
    return {
      loading:   true,
      editing:   false,
      isRenewal: false,
      renewFromId: null,
      renewalMaxGapDays: Constants.RenewalRules.CONTINUITY_MAX_GAP_DAYS,
      dependentSummary: { totalChildren: 0, qualifiedChildren: 0, hasQualifiedChildren: false, children: [] },
      activeTab: 'contract',
      probationError: false,
      // Adenda opcional (solo renovación). Se sube al NUEVO contrato tras crear la renovación.
      addendumFile: null,
      addendumFileKey: 0,
      addendumDescription: 'Adenda de renovación',
      // Adenda multi-campo (DEF-004: delegado a AddendaFieldsForm vía v-model) + motivo.
      adendaFields: { changedFields: [] },
      adendaFieldsKey: 0,
      adendaEffectiveDate: null,
      adendaReason: '',
      // Guarda el sueldo base del contrato antes de que la adenda lo tome (para restaurarlo al desmarcar).
      preAdendaBaseSalary: null,
      // Valores persistidos al cargar (edición): permiten validar el plazo máximo SOLO si cambian tipo o
      // fecha de fin, preservando contratos históricos que ya excedían antes de este fix.
      originalContractType: null,
      originalFinishDate: null,
      cessationDocuments: [],

      tabItems: [
        { key: 'contract',   label: 'Datos del contrato', icon: 'ri-file-list-line' },
        { key: 'location',   label: 'Ubicación laboral',  icon: 'ri-building-line' },
        { key: 'conditions', label: 'Condiciones',        icon: 'ri-money-dollar-circle-line' },
        { key: 'insurance',  label: 'Seguros y salud',    icon: 'ri-heart-pulse-line' },
      ],

      // Catálogos
      employeeList: [],
      companies:    [],
      workSites:    [],
      areas:        [],
      positions:    [],
      managers:     [],
      currentManager: null,
      schedules:    [],
      channels:     [],
      expenseLines: [],
      payrollTypes: [],

      // Renovación: motivos de cese para cerrar el contrato anterior (solo si está vencido).
      cessationReasons: [],
      renewOriginExpired: false,

      contractDurationList: Object.values(Constants.ContractType),

      employeeInfo: {
        identifierType:  null,
        identifier:      null,
        fullName:        null,
        activeContracts: [],
      },

      form: {
        employeeId:       null,
        contractType:     null,
        cessationReasonId: null,
        durationMonths:   null,
        startDate:        null,
        finishDate:       null,
        probationEndDate: null,
        companyId:        null,
        workSiteId:       null,
        areaId:           null,
        positionId:       null,
        directManagerId:  null,
        hrResponsibleEmail: null,
        scheduleId:       null,
        channelId:        null,
        expenseLineId:    null,
        baseSalary:          null,
        salaryCurrency:      'PEN',
        laborRegime:         null,
        occupationalCategory: null,
        workerType:          null,
        workSchedule:        null,
        weeklyHours:         null,
        hasFamilyAllowance:  false,
        specialSituation:    null,
        corporateEmail:      null,
        corporatePhone:      null,
        hasEps:          false,
        epsEntity:       null,
        hasSctrPension:  false,
        hasSctrHealth:   false,
        hasLifeInsurance: false,
        lifeInsurancePolicy: null,
        sctrPensionPolicy:   null,
        sctrHealthPolicy:    null,
        essaludCode:     null,
        payrollTypeIds:  [],
        fixedAmounts:    {},
      },
    }
  },

  validations() {
    return {
      form: {
        employeeId:   { required },
        contractType: { required },
        durationMonths: { numeric },
        startDate:    { required },
        finishDate:   {},
        companyId:    { required },
        areaId:       { required },
        positionId:   { required },
        channelId:    { required },
        expenseLineId: { required },
        baseSalary:   { required, numeric },
        laborRegime:  { required },
        payrollTypeIds: { required },
      },
    }
  },

  computed: {
    adendaChangedFields() {
      return this.adendaFields.changedFields || []
    },
    adendaHasChanges() {
      return this.adendaChangedFields.length > 0
    },
    adendaHasNonDateAware() {
      return this.adendaChangedFields.some(f => NON_DATE_AWARE_FIELDS.includes(f))
    },
    adendaMaxEffectiveDate() {
      return this.adendaHasNonDateAware ? new Date().toISOString().slice(0, 10) : null
    },
    // En renovación con adenda de sueldo, el sueldo base del contrato se toma de la adenda → input bloqueado.
    salaryLockedByAdenda() {
      return this.isRenewal && this.adendaChangedFields.includes('BASE_SALARY')
    },
    // "Sin plazo fijo": indeterminado (01) o por servicio (99) → sin duración ni fecha de fin.
    isIndefiniteContract() {
      const t = Object.values(Constants.ContractType).find(x => x.key === this.form.contractType)
      return t ? !t.fixedTerm : false
    },
    // Contrato traído de BD con fecha de fin pero sin duración (típico de carga masiva,
    // modelo T-Registro). En ese caso preservamos la fecha de BD en vez de recalcularla
    // desde la duración. En cuanto el usuario ingrese una duración, se recalcula normal.
    hasLoadedFinishDateWithoutDuration() {
      return this.editing && !!this.form.finishDate && !this.form.durationMonths
    },
    // Plazo máximo del tipo actual (o null si el tipo no tiene tope legal).
    durationMaxMonths() {
      return MAX_DURATION_MONTHS_BY_TYPE[this.form.contractType] || null
    },
    // ¿El plazo del contrato supera el máximo legal del tipo? (Art. 74 D.S. 003-97-TR). Espeja el backend:
    // fin efectivo = finishDate del form, o inicio + duración - 1 día; máximo permitido = inicio + tope - 1
    // día (el borde exacto es válido). En edición solo valida si cambió el tipo o la fecha de fin.
    durationExceedsMaxByType() {
      if (this.isIndefiniteContract || !this.form.startDate || !this.durationMaxMonths) return false
      const effFinish = this.form.finishDate
        ? moment(this.form.finishDate)
        : (Number(this.form.durationMonths) > 0
            ? moment(this.form.startDate).add(Number(this.form.durationMonths), 'months').subtract(1, 'day')
            : null)
      if (!effFinish || !effFinish.isValid()) return false
      if (this.editing) {
        const typeChanged = this.form.contractType !== this.originalContractType
        const finishChanged = effFinish.format('YYYY-MM-DD') !== this.originalFinishDate
        if (!typeChanged && !finishChanged) return false
      }
      const maxFinish = moment(this.form.startDate).add(this.durationMaxMonths, 'months').subtract(1, 'day')
      return effFinish.isAfter(maxFinish, 'day')
    },
    isCeased() {
      return this.form.displayStatus === 'CESADO'
    },
    tabErrors() {
      const v = this.$v.form
      return [
        v.employeeId.$error || v.contractType.$error || v.startDate.$error || v.finishDate.$error,
        v.companyId.$error  || v.areaId.$error        || v.positionId.$error || v.channelId.$error || v.expenseLineId.$error,
        v.baseSalary.$error || v.laborRegime.$error    || v.payrollTypeIds.$error,
        false,
      ]
    },
    // Semi-wizard (solo en alta = ruta de creación, sin id): botón contextual Siguiente/Guardar.
    isAlta() {
      return !this.$route.params.id
    },
    currentTabIndex() {
      return this.tabItems.findIndex(t => t.key === this.activeTab)
    },
    isLastTab() {
      return this.currentTabIndex === this.tabItems.length - 1
    },
    // Reglas de negocio que bloquean avanzar desde el tab actual (mismas que bloquean Guardar).
    currentTabBlocked() {
      if (this.activeTab === 'contract') return this.probationError || this.durationExceedsMaxByType
      if (this.activeTab === 'location') return this.hasActiveContractInSelectedCompany && !this.editing
      return false
    },
    hasActiveContractInSelectedCompany() {
      // Solo aplica al CREAR: en edición el contrato vigente es el mismo que se edita.
      if (this.editing) return false
      const list = this.employeeInfo.activeContracts || []
      const cid = this.form.companyId
      // En renovación, el contrato anterior (aún vigente) no debe bloquear: queda VENCIDO al
      // llegar su fecha de fin, y el nuevo arranca al día siguiente (sin solape).
      return !!cid && list.some(c => c.companyId === cid && c.id !== this.renewFromId)
    },
    otherActiveContractsText() {
      const list = this.employeeInfo.activeContracts || []
      const cid = this.form.companyId
      const others = list.filter(c => c.companyId !== cid).map(c => c.companyName).filter(Boolean)
      return others.length ? others.join(', ') : ''
    },
  },

  watch: {
    // Sincronización sueldo base ↔ adenda (renovación). Al pasar a SUELDO/AMBOS, el sueldo base del
    // contrato se toma de la adenda (y el input queda bloqueado vía salaryLockedByAdenda). Al salir, se
    // restaura el valor prellenado original.
    // DEF-004: adendaFields viene de AddendaFieldsForm (v-model). Replica la sincronización previa:
    // al activar SUELDO, el sueldo base del contrato se toma de la adenda (input bloqueado vía
    // salaryLockedByAdenda); al desactivar, se restaura el valor prellenado.
    adendaFields: function (now, old) {
      const hasSalary = (now.changedFields || []).includes('BASE_SALARY')
      const hadSalary = ((old && old.changedFields) || []).includes('BASE_SALARY')
      if (hasSalary && !hadSalary) {
        this.preAdendaBaseSalary = this.form.baseSalary // guarda el valor prellenado
      } else if (!hasSalary && hadSalary) {
        this.form.baseSalary = this.preAdendaBaseSalary // restaura el original
      }
      if (hasSalary && now.newBaseSalary != null && now.newBaseSalary !== '') {
        this.form.baseSalary = now.newBaseSalary // refleja el nuevo sueldo en tiempo real
      }
    },
    'form.contractType': function (newVal, oldVal) {
      if (this.isIndefiniteContract) {
        this.form.durationMonths = null
        this.form.finishDate = null
      } else if (!this.hasLoadedFinishDateWithoutDuration) {
        this.setEndDate()
      }
      // Tiempo parcial (02): autocompletar jornada/horas solo por acción del usuario.
      // Editable manualmente luego; al cambiar a otro tipo vuelve a lo normal.
      if (!this.loading) {
        const partTimeKey = Constants.ContractType.TIEMPO_PARCIAL.key
        if (newVal === partTimeKey) {
          this.form.workSchedule = Constants.WorkSchedule.PARCIAL.key
          this.form.weeklyHours = 24
        } else if (oldVal === partTimeKey) {
          this.form.workSchedule = Constants.WorkSchedule.COMPLETA.key
          this.form.weeklyHours = 48
        }
      }
    },
    'form.startDate': function (val) {
      if (val && val.length === 10 && !this.isIndefiniteContract && !this.hasLoadedFinishDateWithoutDuration) this.setEndDate()
    },
    'form.durationMonths': function () {
      if (!this.isIndefiniteContract) this.setEndDate()
    },
  },

  methods: {
    async onEmployeeSelected(employeeId) {
      this.form.employeeId = employeeId
      // Decisión D: el colaborador no puede ser su propio jefe directo
      if (this.form.directManagerId === employeeId) this.form.directManagerId = null
      // Refrescar jefes disponibles (excluye al empleado recién seleccionado)
      this.loadManagers()
      this.dependentSummary = { totalChildren: 0, qualifiedChildren: 0, hasQualifiedChildren: false, children: [] }
      if (!employeeId) {
        this.employeeInfo = { identifierType: null, identifier: null, fullName: null, activeContracts: [] }
        return
      }
      try {
        this.$store.dispatch('app/loading', true)
        const resp = (await EmployeeService.get(employeeId)).payload
        const list = Array.isArray(resp.currentContracts) ? resp.currentContracts : []
        this.employeeInfo = {
          identifierType:  resp.identifierType,
          identifier:      resp.identifier,
          fullName:        resp.fullName || `${resp.lastNamePaternal || ''} ${resp.lastNameMaternal || ''}, ${resp.name || ''}`.toUpperCase(),
          activeContracts: list.map(c => ({
            id:          c.id,
            companyId:   (c.company && c.company.id)   || c.companyId   || null,
            companyName: (c.company && c.company.name) || c.companyName || null,
          })),
        }
        try {
          const summaryResp = await EmployeeDependentService.getSummary(employeeId)
          this.dependentSummary = summaryResp.payload || this.dependentSummary
          if (!this.editing && this.dependentSummary.hasQualifiedChildren) {
            this.form.hasFamilyAllowance = true
          }
        } catch (e) {
          // non-critical
        }
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al cargar el colaborador')
        console.error(ex)
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    async onCompanyChanged(companyId) {
      this.workSites = []
      if (!companyId) { this.form.workSiteId = null; return }
      try {
        const resp = (await WorkSiteService.listByCompanyAndStatus(companyId, 1)).payload || []
        this.workSites = resp.map(w => ({ key: w.id, label: w.name }))
        // Preservar la sede si pertenece a la empresa (precarga en alta/renovación/edición);
        // si no está en la lista (cambio manual de empresa), limpiarla.
        if (!this.workSites.some(w => w.key === this.form.workSiteId)) {
          this.form.workSiteId = null
        }
      } catch (ex) {
        console.error(ex)
        this.form.workSiteId = null
      }
    },

    async loadManagers() {
      try {
        const resp = await EmployeeService.listActiveManagers(this.form.employeeId)
        let list = (resp.payload || []).map(e => ({ key: e.id, label: e.description }))
        // Decisión C: mantener visible el jefe directo actual aunque no esté en la lista
        if (this.form.directManagerId && this.currentManager
            && this.currentManager.key === this.form.directManagerId
            && !list.some(m => m.key === this.form.directManagerId)) {
          list = [this.currentManager, ...list]
        }
        this.managers = list
      } catch (e) {
        console.error(e)
        this.managers = []
      }
    },

    // Fecha de fin = inicio + duración (meses) - 1 día. Solo para contratos a plazo fijo.
    setEndDate() {
      this.form.finishDate = null
      if (this.isIndefiniteContract) return
      const months = Number(this.form.durationMonths)
      if (!this.form.startDate || !months || months <= 0) return
      const newDate = moment(this.form.startDate).add(months, 'months').subtract(1, 'day').format('YYYY-MM-DD')
      setTimeout(() => { this.form.finishDate = newDate }, 100)
    },

    // Semi-wizard: valida SOLO los campos del tab actual y, si están OK y no hay bloqueo de negocio,
    // avanza al siguiente tab. Mismo mapping tab→campos que tabErrors.
    nextTab() {
      const fieldsByTab = {
        contract:   ['employeeId', 'contractType', 'startDate', 'finishDate'],
        location:   ['companyId', 'areaId', 'positionId', 'channelId', 'expenseLineId'],
        conditions: ['baseSalary', 'laborRegime', 'payrollTypeIds'],
      }
      const fields = fieldsByTab[this.activeTab] || []
      fields.forEach(f => { if (this.$v.form[f]) this.$v.form[f].$touch() })
      const hasError = fields.some(f => this.$v.form[f] && this.$v.form[f].$error)
      if (hasError) {
        this.$nextTick(() => {
          const el = document.querySelector('.wz-form-error-msg, .form-error')
          if (el) this.$smoothScroll({ scrollTo: el, updateHistory: false, offset: -50 })
        })
        return
      }
      if (this.currentTabBlocked) return // regla de negocio del tab (el botón ya está deshabilitado)
      const idx = this.currentTabIndex
      if (idx > -1 && idx < this.tabItems.length - 1) {
        this.activeTab = this.tabItems[idx + 1].key
      }
    },

    async save() {
      // Blindaje semi-wizard: si el form se envía en un paso intermedio del alta/renovación
      // (por Enter, autofill, extensión de navegador, o cualquier disparo externo),
      // redirigir a nextTab() en vez de guardar. Ver ticket RRHH renovación paso 3.
      if (this.isAlta && !this.isLastTab) {
        this.nextTab()
        return
      }
      this.$v.$touch()
      if (this.$v.$error) {
        const tabKeys = ['contract', 'location', 'conditions', 'insurance']
        const firstErrorIndex = this.tabErrors.findIndex(e => e)
        if (firstErrorIndex !== -1) this.activeTab = tabKeys[firstErrorIndex]
        this.$nextTick(() => {
          const el = document.querySelector('.wz-form-error-msg, .form-error')
          if (el) this.$smoothScroll({ scrollTo: el, updateHistory: false, offset: -50 })
        })
        return
      }

      // Armar la lista de tipos con monto fijo (modo FIXED) y validar que el monto sea > 0.
      const payrollTypes = (this.form.payrollTypeIds || []).map(id => {
        const pt = this.payrollTypes.find(t => t.id === id)
        const isFixed = pt && pt.calculationMode === 'FIXED'
        return { payrollTypeId: id, fixedAmount: isFixed ? Number(this.form.fixedAmounts[id]) : null }
      })
      const missingFixed = payrollTypes.some(x => {
        const pt = this.payrollTypes.find(t => t.id === x.payrollTypeId)
        return pt && pt.calculationMode === 'FIXED' && !(x.fixedAmount > 0)
      })
      if (missingFixed) {
        this.activeTab = 'conditions'
        Alert.error('Ingrese el monto fijo (mayor a 0) para los tipos de planilla con monto fijo.')
        return
      }

      // Renovación de contrato vencido: el motivo de cese del contrato anterior es obligatorio.
      if (this.isRenewal && this.renewOriginExpired && !this.form.cessationReasonId) {
        this.activeTab = 'contract'
        Alert.error('Seleccione el motivo de cese del contrato anterior.')
        return
      }

      // Fecha de fin editable manualmente (requisito RRHH). Solo aplica a plazo fijo.
      // Si RRHH dejó la fecha auto-calculada (inicio + duración - 1 día) se conserva la duración;
      // si la cambió a otra fecha, se limpia la duración y el contrato queda con fecha de fin
      // explícita (al recargar, Duración aparece vacía y Fecha de fin es la editada).
      // Para indeterminado/por servicio no hay fecha de fin ni duración.
      let finishDate = this.form.finishDate ? moment(this.form.finishDate).format('YYYY-MM-DD') : null
      let durationMonths = this.form.durationMonths
      if (this.isIndefiniteContract) {
        finishDate = null
        durationMonths = null
      } else if (finishDate) {
        const months = Number(this.form.durationMonths)
        const autoFinish = (this.form.startDate && months > 0)
          ? moment(this.form.startDate).add(months, 'months').subtract(1, 'day').format('YYYY-MM-DD')
          : null
        // Edición manual = la fecha de fin no coincide con el auto-cálculo => limpiar duración.
        if (!autoFinish || finishDate !== autoFinish) durationMonths = null
      }

      // Validación: la fecha de fin debe ser posterior a la de inicio (solo plazo fijo).
      if (!this.isIndefiniteContract && finishDate && this.form.startDate
          && !moment(finishDate).isAfter(this.form.startDate)) {
        this.activeTab = 'contract'
        Alert.error('La fecha de fin debe ser posterior a la fecha de inicio.')
        return
      }

      // Validación: plazo máximo legal por tipo (Art. 74 D.S. 003-97-TR). Espeja el backend; en edición
      // solo bloquea si cambió el tipo o la fecha de fin (ver computed durationExceedsMaxByType).
      if (this.durationExceedsMaxByType) {
        this.activeTab = 'contract'
        Alert.error(`El plazo del contrato tipo ${this.form.contractType} supera el máximo legal de `
          + `${this.durationMaxMonths} meses según Art. 74 D.S. 003-97-TR.`)
        return
      }

      // Adenda opcional (renovación): si se adjuntó, validar en cliente ANTES de crear la renovación,
      // para no crearla con una adenda condenada a fallar. Espeja las validaciones del backend.
      if (this.isRenewal && this.addendumFile) {
        if (!this.isPdfFile(this.addendumFile)) {
          Alert.error('El documento de adenda debe ser un PDF.')
          return
        }
        if (this.addendumFile.size > MAX_ADDENDUM_SIZE) {
          Alert.error('El documento de adenda no debe superar los 100MB.')
          return
        }
      }
      // Adenda multi-campo: si hay campos marcados, validar valores + fecha + exigir archivo (el backend
      // materializa la adenda por el endpoint multipart, que requiere el PDF).
      if (this.isRenewal && this.adendaHasChanges) {
        if (!this.adendaEffectiveDate) {
          Alert.error('La fecha efectiva es obligatoria cuando la adenda cambia campos.')
          return
        }
        if (this.adendaHasNonDateAware && this.adendaEffectiveDate > new Date().toISOString().slice(0, 10)) {
          Alert.error('Los cambios de área/canal/línea/sede/jefe/responsable no admiten fecha futura.')
          return
        }
        const adendaFieldError = this.$refs.adendaFields && this.$refs.adendaFields.validate()
        if (adendaFieldError) { Alert.error(adendaFieldError + '.'); return }
        if (!this.addendumFile) {
          Alert.error('Adjunta el PDF de la adenda para registrar los cambios.')
          return
        }
      }

      try {
        this.$store.dispatch('app/loading', true)
        // En modo renovación se envía el id del contrato origen para decidir la reapertura del período.
        const data = { ...this.form, finishDate, durationMonths, payrollTypes, renewFromId: this.isRenewal ? this.renewFromId : null }
        console.log('[probation-check] form.probationEndDate antes de enviar =', this.form.probationEndDate)
        let response
        if (!this.editing) response = await ContractsService.save(data)
        else response = await ContractsService.update(this.$route.params.id, data)
        if (response.status === 'BAD_REQUEST') {
          Alert.error('Error de validación: revise los campos')
          return
        }
        Alert.success('Contrato guardado correctamente')
        if (!this.editing) {
          const newContractId = response.payload && response.payload.id
          // Renovación con adenda adjunta: subirla al NUEVO contrato. La subida NO revierte la renovación.
          if (this.isRenewal && this.addendumFile && newContractId) {
            await this.uploadRenewalAddendum(newContractId)
          } else {
            this.$router.push({ name: 'contracts-list' })
          }
        }
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al guardar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    // Sube la adenda al contrato recién renovado. Caso OK → success + lista. Caso falla → warning y
    // redirige al detalle del nuevo contrato (donde puede reintentar). Nunca revierte la renovación.
    async uploadRenewalAddendum(newContractId) {
      try {
        const f = this.adendaFields
        await ContractAddendumService.upload(
          newContractId, this.addendumFile, this.addendumDescription.trim() || null, {
            changedFields: f.changedFields,
            newBaseSalary: f.newBaseSalary,
            newPositionId: f.newPositionId,
            newAreaId: f.newAreaId,
            newChannelId: f.newChannelId,
            newExpenseLineId: f.newExpenseLineId,
            newWorkSiteId: f.newWorkSiteId,
            newDirectManagerId: f.newDirectManagerId,
            newHrResponsibleEmail: f.newHrResponsibleEmail,
            effectiveDate: this.adendaHasChanges ? this.adendaEffectiveDate : null,
            reason: this.adendaReason && this.adendaReason.trim() ? this.adendaReason.trim() : null,
          })
        Alert.success('Adenda subida correctamente')
        this.$router.push({ name: 'contracts-list' })
      } catch (addErr) {
        console.error(addErr)
        Alert.alert('Advertencia', 'Renovación creada correctamente, pero la adenda no se pudo subir. '
          + 'Podés subirla desde el detalle del contrato.')
        this.$router.push({ name: 'contracts-detail', params: { id: newContractId } })
      }
    },

    onAddendumFileChange(files) {
      this.addendumFile = files && files.length > 0 ? files[0] : null
    },

    isPdfFile(file) {
      const byType = file.type === 'application/pdf'
      const byName = (file.name || '').toLowerCase().endsWith('.pdf')
      return byType || byName
    },

    async downloadCessationDoc(doc) {
      try {
        await this.$store.dispatch('app/loading', true)
        const response = await ContractsService.downloadCessationDocument(this.$route.params.id, doc.id)
        fileDownload(response.data, doc.name || Util.getFileName(response))
      } catch (error) {
        console.error(error)
        Alert.error('Error al descargar el documento de cese')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    // Renovación: crea un contrato NUEVO precargado con los datos del anterior. No marca `editing`
    // (el flujo de guardado es el de crear). El período de empleo no se toca (no hubo cese).
    async loadForRenewal(renewFromId) {
      try {
        this.$store.dispatch('app/loading', true)
        const payload = (await ContractsService.get(renewFromId)).payload
        this.form = {
          ...this.form,
          ...payload,
          employeeId:      payload.employee      ? payload.employee.id      : null,
          companyId:       payload.company       ? payload.company.id       : null,
          areaId:          payload.area          ? payload.area.id          : null,
          positionId:      payload.position      ? payload.position.id      : null,
          workSiteId:      payload.workSite      ? payload.workSite.id      : null,
          directManagerId: payload.directManager ? payload.directManager.id : null,
          scheduleId:      payload.schedule      ? payload.schedule.id      : null,
          channelId:       payload.channelId     || null,
          expenseLineId:   payload.expenseLineId || null,
          payrollTypeIds:  payload.payrollTypeIds || [],
          fixedAmounts:    payload.payrollTypeFixedAmounts || {},
          // Campos que NO se heredan: el contrato nuevo los define desde cero.
          id:               null,
          number:           null,
          status:           null,
          terminationDate:  null,
          finishDate:       null,
          durationMonths:   null,
          probationEndDate: null,
          hasSignedDocument: false,
          cessationReasonId: null,
          // Inicio = fin del contrato anterior + 1 día.
          startDate: payload.finishDate
            ? moment(payload.finishDate).add(1, 'day').format('YYYY-MM-DD')
            : null,
        }
        this.currentManager = payload.directManager
          ? { key: payload.directManager.id, label: payload.directManager.description }
          : null
        // Si el contrato anterior está VENCIDO, RRHH elige con qué motivo se cierra (default "07").
        this.renewOriginExpired = payload.displayStatus === 'VENCIDO'
        if (this.renewOriginExpired) await this.loadCessationReasons()
        if (this.form.companyId) await this.onCompanyChanged(this.form.companyId)
        if (this.form.employeeId) await this.onEmployeeSelected(this.form.employeeId)
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el contrato a renovar')
        this.$router.push({ name: 'contracts-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
        setTimeout(() => { this.loading = false }, 1000)
      }
    },

    // Motivos de cese activos para el cierre del contrato anterior en la renovación.
    // Default: el motivo con código T-Registro "07" (fin de contrato) → mantiene antigüedad.
    async loadCessationReasons() {
      try {
        const reasons = (await CessationReasonService.list({ status: 1 })).payload || []
        this.cessationReasons = reasons.map(r => ({ key: r.id, label: r.name }))
        const byExpiry = reasons.find(r => r.tregistroCode === '07')
        if (byExpiry) this.form.cessationReasonId = byExpiry.id
      } catch (e) {
        console.error(e)
        this.cessationReasons = []
      }
    },

    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const payload = (await ContractsService.get(id)).payload
        this.form = {
          ...payload,
          employeeId:      payload.employee      ? payload.employee.id      : null,
          companyId:       payload.company       ? payload.company.id       : null,
          areaId:          payload.area          ? payload.area.id          : null,
          positionId:      payload.position      ? payload.position.id      : null,
          workSiteId:      payload.workSite      ? payload.workSite.id      : null,
          directManagerId: payload.directManager ? payload.directManager.id : null,
          scheduleId:      payload.schedule      ? payload.schedule.id      : null,
          channelId:       payload.channelId     || null,
          expenseLineId:   payload.expenseLineId || null,
          payrollTypeIds:  payload.payrollTypeIds || [],
          fixedAmounts:    payload.payrollTypeFixedAmounts || {},
        }
        this.cessationDocuments = payload.cessationDocuments || []
        this.editing = true
        // Snapshot para la validación de plazo máximo: solo se revalida si cambian tipo o fecha de fin.
        this.originalContractType = payload.contractType || null
        this.originalFinishDate = payload.finishDate ? moment(payload.finishDate).format('YYYY-MM-DD') : null
        // Decisión C: conservar el jefe actual (label) por si no está en la lista filtrada
        this.currentManager = payload.directManager
          ? { key: payload.directManager.id, label: payload.directManager.description }
          : null
        if (this.form.companyId) await this.onCompanyChanged(this.form.companyId)
        if (this.form.employeeId) await this.onEmployeeSelected(this.form.employeeId)
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar')
        this.$router.push({ name: 'contracts-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
        setTimeout(() => { this.loading = false }, 1000)
      }
    },
  },

  async created() {
    const renewFromId = this.$route.query.renewFromId
    if (this.$route.params.id) {
      this.load(this.$route.params.id)
    } else if (renewFromId) {
      this.isRenewal = true
      this.renewFromId = Number(renewFromId)
      this.loadForRenewal(this.renewFromId)
    } else {
      this.loading = false
    }

    // Jefe directo: independiente de los demás catálogos (no debe bloquearse si uno falla)
    this.loadManagers()

    try {
      const [employees, companies, areas, positions, schedules, channelsResp, expenseLinesResp, payrollTypesResp] = await Promise.all([
        EmployeeService.listAll(),
        CompanyService.listActive(),
        AreaService.listActive(),
        PositionService.listAll(),
        WorkScheduleService.listActive(),
        ChannelService.listActive(),
        ExpenseLineService.listActive(),
        PayrollTypeService.listActive(),
      ])
      this.employeeList = (employees.payload || []).map(e => ({ key: e.id, label: e.description }))
      this.companies    = (companies.payload || []).map(c => ({ key: c.id, label: c.name }))
      this.areas        = (areas.payload    || []).map(a => ({ key: a.id, label: a.description }))
      this.positions    = (positions.payload || []).map(p => ({ key: p.id, label: p.description }))
      this.schedules    = (schedules.payload || []).map(s => ({ key: s.id, label: s.name, ...s }))
      this.channels     = (channelsResp.payload || []).map(c => ({ key: c.id, label: c.name }))
      this.expenseLines = (expenseLinesResp.payload || []).map(e => ({ key: e.id, label: e.name }))
      this.payrollTypes = payrollTypesResp.payload || []
    } catch (ex) {
      console.error(ex)
      Alert.error('Error al cargar los catálogos')
    }
  },
}
</script>

<style lang="stylus" scoped></style>
